import React from "react";
import { Link } from "react-router-dom";
import { ArrowRight } from "lucide-react";
import Marquee from "../components/Marquee";

const BG_IMG =
  "https://images.unsplash.com/photo-1589829545856-d10d557cf95f?crop=entropy&cs=srgb&fm=jpg&q=85&w=1200";

const keyTerms = [
  {
    term: "Market Failure",
    def: "When the free market fails to allocate resources efficiently, leading to outcomes harmful to society.",
  },
  {
    term: "Externality",
    def: "A cost or benefit that falls on third parties not directly involved in a transaction (e.g., pollution from a factory).",
  },
  {
    term: "Deregulation",
    def: "The reduction or elimination of government rules and restrictions on an industry or sector.",
  },
  {
    term: "Antitrust",
    def: "Laws designed to prevent monopolies and promote competition in the marketplace.",
  },
  {
    term: "Price Ceiling",
    def: "A government-set maximum price that can be charged for a good or service.",
  },
  {
    term: "Compliance Cost",
    def: "The expense businesses incur to follow government regulations, including paperwork, staffing, and modifications.",
  },
];

export default function Background() {
  return (
    <div style={{ backgroundColor: "#050505", paddingTop: "64px" }}>
      {/* Page Header */}
      <div
        data-testid="background-header"
        style={{
          position: "relative",
          padding: "64px 24px 56px",
          borderBottom: "1px solid #1A1A1A",
          overflow: "hidden",
        }}
      >
        <div
          style={{
            position: "absolute",
            inset: 0,
            backgroundImage: `url(${BG_IMG})`,
            backgroundSize: "cover",
            backgroundPosition: "center",
            opacity: 0.07,
          }}
        />
        <div style={{ position: "relative", maxWidth: "1280px", margin: "0 auto" }}>
          <p
            style={{
              fontFamily: "'IBM Plex Mono', monospace",
              fontSize: "11px",
              letterSpacing: "0.18em",
              textTransform: "uppercase",
              color: "#FF3B30",
              marginBottom: "16px",
            }}
          >
            Page 02 — Background
          </p>
          <h1
            style={{
              fontFamily: "'Cabinet Grotesk', sans-serif",
              fontSize: "clamp(2.2rem, 5vw, 4rem)",
              fontWeight: 900,
              letterSpacing: "-0.03em",
              color: "#FFFFFF",
              marginBottom: "16px",
              lineHeight: 1.05,
            }}
          >
            Understanding Government Regulation
          </h1>
          <p
            style={{
              fontSize: "16px",
              color: "#71717A",
              maxWidth: "600px",
              lineHeight: 1.65,
            }}
          >
            Before exploring how regulation affects specific groups, it's
            important to understand what government regulation actually is, why
            it exists, and how it has evolved in U.S. history.
          </p>
        </div>
      </div>

      <Marquee />

      {/* Main Content */}
      <div
        style={{
          maxWidth: "1280px",
          margin: "0 auto",
          padding: "64px 24px",
          display: "grid",
          gridTemplateColumns: "1fr 320px",
          gap: "64px",
          alignItems: "start",
        }}
        className="bg-layout"
      >
        {/* Article */}
        <article data-testid="background-article">
          <section style={{ marginBottom: "56px" }}>
            <h2
              style={{
                fontFamily: "'Cabinet Grotesk', sans-serif",
                fontSize: "clamp(1.5rem, 3vw, 2rem)",
                fontWeight: 800,
                color: "#FFFFFF",
                marginBottom: "20px",
                paddingBottom: "12px",
                borderBottom: "1px solid #1A1A1A",
              }}
            >
              What Is Government Regulation?
            </h2>
            <p style={{ color: "#D1D1D6", lineHeight: 1.8, marginBottom: "16px", fontSize: "15px" }}>
              Government regulation refers to rules, laws, and standards created
              by federal, state, or local agencies that govern how businesses
              operate, what products can be sold, and how industries function.
              These regulations are enforced by agencies like the Environmental
              Protection Agency (EPA), the Food and Drug Administration (FDA),
              the Occupational Safety and Health Administration (OSHA), and the
              Federal Trade Commission (FTC).
            </p>
            <p style={{ color: "#D1D1D6", lineHeight: 1.8, marginBottom: "16px", fontSize: "15px" }}>
              At its core, regulation exists to address{" "}
              <strong style={{ color: "#FFFFFF" }}>market failures</strong> —
              situations where the free market produces outcomes that are
              inefficient, unsafe, or unfair. Without any rules, a company
              might dump toxic waste into a river because it's cheaper, or sell
              dangerous products to customers who can't detect the risk. Regulation
              steps in to prevent these outcomes.
            </p>
            <blockquote>
              "The first duty of government is to protect people from each other
              and from themselves." — A common principle behind U.S. regulatory
              philosophy.
            </blockquote>
            <p style={{ color: "#D1D1D6", lineHeight: 1.8, fontSize: "15px" }}>
              That said, regulation is not without trade-offs. Every rule
              imposes costs — on businesses that must comply, on consumers who
              may pay higher prices, and on workers whose jobs may be affected.
              Understanding these trade-offs is central to this project.
            </p>
          </section>

          <section style={{ marginBottom: "56px" }}>
            <h2
              style={{
                fontFamily: "'Cabinet Grotesk', sans-serif",
                fontSize: "clamp(1.5rem, 3vw, 2rem)",
                fontWeight: 800,
                color: "#FFFFFF",
                marginBottom: "20px",
                paddingBottom: "12px",
                borderBottom: "1px solid #1A1A1A",
              }}
            >
              Types of Regulation
            </h2>
            {[
              {
                type: "Economic Regulation",
                color: "#FF3B30",
                desc: "Controls prices, market entry, and competitive behavior in specific industries. Examples include antitrust laws that break up monopolies, utility rate regulations that cap what power companies can charge, and financial regulations that limit what banks can do with deposits.",
              },
              {
                type: "Social Regulation",
                color: "#FFCC00",
                desc: "Protects public health, safety, and the environment regardless of industry. This category includes OSHA workplace safety standards, EPA environmental rules, and FDA drug approval processes. Social regulation is often the most visible and controversial type.",
              },
              {
                type: "Financial Regulation",
                color: "#007AFF",
                desc: "Oversees banking, securities markets, and financial services. After the 2008 financial crisis, Congress passed the Dodd-Frank Act, creating new rules for banks and establishing the Consumer Financial Protection Bureau (CFPB) to protect everyday borrowers.",
              },
              {
                type: "Consumer Protection",
                color: "#FF3B30",
                desc: "Ensures businesses treat consumers fairly. The FTC enforces rules against false advertising and deceptive practices, while the CFPB investigates illegal financial activities targeting consumers.",
              },
            ].map((item) => (
              <div
                key={item.type}
                style={{
                  marginBottom: "24px",
                  padding: "24px",
                  backgroundColor: "#0D0D0D",
                  borderLeft: `3px solid ${item.color}`,
                }}
              >
                <h3
                  style={{
                    fontFamily: "'Cabinet Grotesk', sans-serif",
                    fontSize: "17px",
                    fontWeight: 800,
                    color: item.color,
                    marginBottom: "10px",
                  }}
                >
                  {item.type}
                </h3>
                <p style={{ color: "#A1A1AA", fontSize: "14px", lineHeight: 1.75 }}>
                  {item.desc}
                </p>
              </div>
            ))}
          </section>

          <section style={{ marginBottom: "56px" }}>
            <h2
              style={{
                fontFamily: "'Cabinet Grotesk', sans-serif",
                fontSize: "clamp(1.5rem, 3vw, 2rem)",
                fontWeight: 800,
                color: "#FFFFFF",
                marginBottom: "20px",
                paddingBottom: "12px",
                borderBottom: "1px solid #1A1A1A",
              }}
            >
              A Brief History of U.S. Regulation
            </h2>
            {[
              {
                year: "1890",
                event: "Sherman Antitrust Act",
                desc: "The first major federal regulation targeting monopolies. It was used to break up Standard Oil in 1911 and AT&T in 1984, demonstrating that government was willing to step in when corporations gained too much power.",
              },
              {
                year: "1930s",
                event: "New Deal Era",
                desc: "Following the Great Depression, President Franklin D. Roosevelt created the Securities and Exchange Commission (SEC), the Federal Deposit Insurance Corporation (FDIC), and dozens of other agencies. This era established the idea that government had a responsibility to stabilize the economy.",
              },
              {
                year: "1970s",
                event: "Great Society Expansions",
                desc: "Congress created the EPA (1970), OSHA (1970), and the Consumer Product Safety Commission (1972). These agencies focused on social regulation — protecting workers, consumers, and the environment from corporate harm.",
              },
              {
                year: "1980s",
                event: "Reagan Deregulation",
                desc: "President Reagan championed reducing government regulations, arguing they stifled economic growth. His administration eased rules on airlines, telecommunications, and banking — some of which led to both greater competition and, later, the savings-and-loan crisis.",
              },
              {
                year: "2010",
                event: "Dodd-Frank Act",
                desc: "In response to the 2008 financial crisis, Congress passed sweeping new financial regulations. The law required banks to hold more capital, limited risky trading, and created the CFPB — representing the largest expansion of financial regulation since the New Deal.",
              },
            ].map((item) => (
              <div
                key={item.year}
                data-testid={`history-${item.year}`}
                style={{
                  display: "grid",
                  gridTemplateColumns: "80px 1fr",
                  gap: "20px",
                  marginBottom: "28px",
                  paddingBottom: "28px",
                  borderBottom: "1px solid #1A1A1A",
                }}
              >
                <div>
                  <span
                    style={{
                      fontFamily: "'IBM Plex Mono', monospace",
                      fontSize: "13px",
                      fontWeight: 500,
                      color: "#FF3B30",
                    }}
                  >
                    {item.year}
                  </span>
                </div>
                <div>
                  <h4
                    style={{
                      fontFamily: "'Cabinet Grotesk', sans-serif",
                      fontSize: "16px",
                      fontWeight: 700,
                      color: "#FFFFFF",
                      marginBottom: "8px",
                    }}
                  >
                    {item.event}
                  </h4>
                  <p style={{ color: "#A1A1AA", fontSize: "14px", lineHeight: 1.75 }}>
                    {item.desc}
                  </p>
                </div>
              </div>
            ))}
          </section>

          <div
            style={{
              display: "flex",
              gap: "16px",
              flexWrap: "wrap",
              paddingTop: "16px",
            }}
          >
            <Link
              to="/impact-businesses"
              data-testid="next-page-businesses"
              style={{
                display: "inline-flex",
                alignItems: "center",
                gap: "8px",
                backgroundColor: "#FF3B30",
                color: "#FFFFFF",
                padding: "12px 24px",
                fontSize: "13px",
                fontWeight: 600,
                textDecoration: "none",
              }}
            >
              Next: Impact on Businesses <ArrowRight size={14} />
            </Link>
          </div>
        </article>

        {/* Sidebar — Key Terms */}
        <aside data-testid="key-terms-sidebar">
          <div
            style={{
              position: "sticky",
              top: "88px",
              border: "1px solid #27272A",
              backgroundColor: "#0D0D0D",
              padding: "32px",
            }}
          >
            <p
              style={{
                fontFamily: "'IBM Plex Mono', monospace",
                fontSize: "10px",
                letterSpacing: "0.18em",
                textTransform: "uppercase",
                color: "#52525B",
                marginBottom: "24px",
              }}
            >
              Key Terms
            </p>
            {keyTerms.map((item, i) => (
              <div
                key={i}
                data-testid={`key-term-${i}`}
                style={{
                  marginBottom: "20px",
                  paddingBottom: "20px",
                  borderBottom: i < keyTerms.length - 1 ? "1px solid #1A1A1A" : "none",
                }}
              >
                <h4
                  style={{
                    fontFamily: "'Cabinet Grotesk', sans-serif",
                    fontSize: "14px",
                    fontWeight: 700,
                    color: "#FFCC00",
                    marginBottom: "6px",
                  }}
                >
                  {item.term}
                </h4>
                <p style={{ color: "#71717A", fontSize: "12px", lineHeight: 1.65 }}>
                  {item.def}
                </p>
              </div>
            ))}
          </div>
        </aside>
      </div>

      <style>{`
        @media (max-width: 900px) {
          .bg-layout {
            grid-template-columns: 1fr !important;
          }
        }
      `}</style>
    </div>
  );
}
